/**
 * Analytics tracking utility
 * Tracks page views, events, and conversions
 */

import { ADS_CONVERSION_ID, ADS_CONVERSIONS } from '../config/adsConversions';

const BACKEND_URL = (typeof window !== 'undefined' ? window.location.origin : '') || import.meta.env.VITE_REACT_APP_BACKEND_URL;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('corvana_session_id');
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('corvana_session_id', sessionId);
    
    // Start new session
    startSession(sessionId);
  }
  
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }
  return 'Desktop';
};

// Start analytics session
const startSession = async (sessionId) => {
  try {
    await fetch(`${BACKEND_URL}/api/analytics/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        first_page: window.location.href,
        referrer: document.referrer || '(direct)',
        device_type: getDeviceType()
      })
    });
  } catch (error) {
    console.error('Failed to start analytics session:', error);
  }
};

// Track page view
export const trackPageView = async (pageUrl = window.location.href) => {
  const sessionId = getSessionId();
  
  try {
    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'page_view',
        page_url: pageUrl,
        referrer: document.referrer || '(direct)',
        user_agent: navigator.userAgent,
        device_type: getDeviceType(),
        session_id: sessionId
      })
    });
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

// Track custom event
export const trackEvent = async (eventType, metadata = {}) => {
  const sessionId = getSessionId();
  
  try {
    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        page_url: window.location.href,
        referrer: document.referrer || '(direct)',
        user_agent: navigator.userAgent,
        device_type: getDeviceType(),
        session_id: sessionId,
        metadata: metadata
      })
    });
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

// Track enquiry conversion
export const trackEnquiryConversion = async (enquiryId) => {
  const sessionId = getSessionId();
  
  try {
    // Mark session as converted
    await fetch(`${BACKEND_URL}/api/analytics/enquiry/converted`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        enquiry_id: enquiryId
      })
    });
    
    // Track conversion event
    await trackEvent('enquiry_submitted', { enquiry_id: enquiryId });
  } catch (error) {
    console.error('Failed to track enquiry conversion:', error);
  }
};

// Track button click
export const trackButtonClick = async (buttonName, buttonLocation) => {
  await trackEvent('button_click', {
    button_name: buttonName,
    button_location: buttonLocation
  });
};

// Google Analytics 4 Integration (optional)
export const initGoogleAnalytics = (measurementId) => {
  if (!measurementId) return;
  
  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', measurementId);
};

// Initialize analytics on page load
export const initAnalytics = () => {
  // Track initial page view
  trackPageView();
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      trackPageView();
    }
  });
};

/**
 * Fire a Google Ads conversion for one of the actions defined in
 * config/adsConversions.js (enquiry | waitlist_signup | demo_booking).
 * Safe to call anywhere: no-ops when gtag isn't loaded / SSR.
 */
export const trackAdsConversion = (key, params = {}) => {
  if (typeof window === 'undefined') return;
  const action = ADS_CONVERSIONS[key];
  const sendTo = action && action.label
    ? `${ADS_CONVERSION_ID}/${action.label}`
    : ADS_CONVERSION_ID;
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: sendTo, ...params });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'corvana_conversion', conversion_type: key, ...params });
  } catch (e) { /* noop */ }
  // Also record in Corvana's own first-party analytics
  trackEvent(`ads_conversion_${key}`, params);
};
